import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { products, categories } from '../data/products';
import { demoPages } from '../data/demoPages';
import { ProductCard } from '../components/home/ProductCard';
import { ProductPageModal } from '../components/home/ProductPageModal';
import { Button } from '../components/ui/button';
import UserNavMenu from '../components/UserNavMenu';
import { Sparkles, Heart, Search, ArrowRight, SlidersHorizontal } from 'lucide-react';
import LumeraLogo from '../components/LumeraLogo';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'popular', label: 'Most popular' },
  { value: 'az', label: 'A–Z' },
];

const Favorites = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { favorites, loading } = useFavorites();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [search, setSearch] = useState('');
  const [sortBy, setBlackBy] = useState('newest');
  const [categoryFilter, setCategoryFilter] = useState('All');

  const favoriteProducts = useMemo(() => {
    let items = products.filter(p => favorites.has(p.title));

    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(p =>
        p.title.toLowerCase().includes(q) ||
        p.sub.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q)
      );
    }

    if (categoryFilter !== 'All') {
      items = items.filter(p => p.category === categoryFilter);
    }

    if (sortBy === 'az') {
      items.sort((a, b) => a.title.localeCompare(b.title, 'en'));
    } else if (sortBy === 'popular') {
      items.sort((a, b) => a.title.localeCompare(b.title, 'en'));
    }
    // 'newest' = default Set insertion order (most recent first from API)

    return items;
  }, [favorites, search, sortBy, categoryFilter]);

  const availableCategories = useMemo(() => {
    const cats = new Set(products.filter(p => favorites.has(p.title)).map(p => p.category));
    return ['All', ...Array.from(cats).sort((a, b) => a.localeCompare(b, 'en'))];
  }, [favorites]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F7F5F2] dark:bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F7F5F2] dark:bg-background" data-testid="favorites-page">
      {/* Nav */}
      <nav className="bg-white dark:bg-card border-b border-[#d8cfc8]/50 dark:border-border/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <LumeraLogo size="default" />
          <UserNavMenu size={34} />
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                <Heart className="w-5 h-5 text-red-500 fill-red-500" />
              </div>
              <h1 className="text-3xl font-serif font-bold text-stone-900">My Favorites</h1>
            </div>
            <p className="text-stone-500 font-sans text-sm">
              {favorites.size === 0
                ? 'You have no favorites yet'
                : `${favorites.size} ${favorites.size === 1 ? 'favorite' : 'favorites'} saved`}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        ) : favorites.size === 0 ? (
          /* Empty state */
          <div className="text-center py-20" data-testid="favorites-empty">
            <div className="w-20 h-20 rounded-full bg-stone-50 flex items-center justify-center mx-auto mb-6">
              <Heart className="w-10 h-10 text-stone-300" />
            </div>
            <h3 className="text-xl font-serif font-semibold text-stone-800 mb-2">No favorites yet</h3>
            <p className="text-stone-500 font-sans mb-8 max-w-md mx-auto">
              Browse the marketplace and tap the heart to save products you like.
            </p>
            <Button
              data-testid="browse-marketplace-btn"
              onClick={() => navigate('/')}
              className="bg-[#B49A88] hover:bg-[#B49A88] text-white rounded-full px-8 py-3 font-sans font-semibold shadow-lg"
            >
              Explore the marketplace <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        ) : (
          <>
            {/* Filters bar */}
            <div className="flex flex-col sm:flex-row gap-3 mb-8" data-testid="favorites-filters">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <input
                  data-testid="favorites-search"
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search favorites..."
                  className="w-full pl-10 pr-4 py-2.5 border border-stone-200 rounded-xl font-sans text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none focus:border-[#B49A88] transition-colors bg-white"
                />
              </div>

              {/* Category filter */}
              <div className="relative">
                <SlidersHorizontal className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
                <select
                  data-testid="favorites-category-filter"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="pl-9 pr-8 py-2.5 border border-stone-200 rounded-xl font-sans text-sm text-stone-700 bg-white focus:outline-none focus:border-[#B49A88] appearance-none cursor-pointer"
                >
                  {availableCategories.map(c => (
                    <option key={c} value={c}>{c === 'All' ? 'All categories' : c}</option>
                  ))}
                </select>
              </div>

              {/* Black */}
              <select
                data-testid="favorites-sort"
                value={sortBy}
                onChange={(e) => setBlackBy(e.target.value)}
                className="px-4 py-2.5 border border-stone-200 rounded-xl font-sans text-sm text-stone-700 bg-white focus:outline-none focus:border-[#B49A88] appearance-none cursor-pointer"
              >
                {SORT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* Results */}
            {favoriteProducts.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-stone-500 font-sans">No favorites match your search.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="favorites-grid">
                {favoriteProducts.map(product => (
                  <ProductCard
                    key={product.title}
                    {...product}
                    onClick={() => setSelectedProduct(product)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Product modal */}
      {selectedProduct && demoPages[selectedProduct.title] && (
        <ProductPageModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onCreateProduct={() => {
            navigate(`/generator?template=${encodeURIComponent(selectedProduct.title)}`);
          }}
          onNextProduct={() => {
            const idx = favoriteProducts.findIndex(p => p.title === selectedProduct.title);
            const next = favoriteProducts[(idx + 1) % favoriteProducts.length];
            setSelectedProduct(next);
          }}
          allProducts={favoriteProducts}
        />
      )}
    </div>
  );
};

export default Favorites;
